import { HeroSection, ImageDescription } from 'components/structures/general';
import {
  devOpsImage,
  devOpsImage2x,
  devOpsKnowBgSvg,
  devOpsKnowImage,
  heroBgDark,
  heroBgLight,
  heroPhone1x,
  heroPhone2x,
  howWorksBg,
  howWorksImage,
  howWorksImage2x,
  solutionsImage,
  solutionsImage2x,
  technologiesToolsBgSvg,
  technologiesToolsImage,
} from 'images/it-infrastructure';
import { bgAboutUs } from 'images/main';
import { NextPage } from 'next';
import React from 'react';
import { DevOpsCards, DevOpsKnow, TechnologiesTools } from 'templates/it-infrastructure';
import { RequestForm, SEO } from 'templates/layout';

const ItInfrastructure: NextPage = () => {
  return (
    <>
      <SEO
        title="IT Infrastructure Services"
        description="Discover next generation infrastructure services. Enhance your computer networks, improve data security and boost software performance – an all-in-one package."
      />
      <HeroSection
        title="IT Infrastructure Services"
        description="An intelligent IT infrastructure offers flexible solutions that facilitate quicker adaptation to new and more effective business models. We review a customer's existing assets from the ground up and develop the right end-to-end infrastructure services."
        bgImage={{ dark: heroBgDark, light: heroBgLight }}
        phoneImage={{ x1: heroPhone1x, x2: heroPhone2x }}
      />
      <ImageDescription
        image={{ image: howWorksImage, srcSet: `${howWorksImage} 1x, ${howWorksImage2x} 2x` }}
        bgImage={{ image: howWorksBg }}
        upperTitle="Step by step"
        title="How it works"
        text={
          'Our qualified Cloud technology specialists (AWS, Azure, GCP) follow all the tried and tested DevOps practices. Prior to starting any project, we meticulously calculate all the long and short-term objectives to outline our priorities and ensure that everything goes as smoothly as planned.\n\n Our DevOps methods are primarily inspired by the Agile and Lean principles, which means that we implement those techniques throughout the whole product development lifecycle.\n\n The foundation of DevOps is based on active collaboration between business owners and development, operations, and quality assurance teams. Our operation experts calculate our software development approach to align all the goals and work into a synchronized and streamlined plan that gets things done on time and around any budget.'
        }
      />
      <ImageDescription
        align="right"
        image={{ image: devOpsImage, srcSet: `${devOpsImage} 1x, ${devOpsImage2x} 2x`, imageCenterLg: true }}
        upperTitle="Progressive development"
        title="What DevOps Means for Your Business"
        text={
          'DevOps services help businesses improve their software solutions and crank things up a notch to get the best from both the development and operations. By implementing DevOps into our daily operations, the US Software Solutions can provide a custom-tailored approach to every project we intend to work on.'
        }
      />
      <ImageDescription
        title="Our DevOps Know-how"
        image={{ image: devOpsKnowImage, imageCenterLg: true }}
        bgImage={{ image: devOpsKnowBgSvg }}
      >
        <DevOpsKnow
          contents={[
            {
              title: 'DevOps advisory - ',
              text: 'By assessing the existing infrastructure and delivery pipeline, we can develop a roadmap that significantly automates project deployment.',
            },
            {
              title: 'Implementation automation - ',
              text: "To achieve full delivery automation and eradicate lapses and downtimes, we're integrating customers' departments that work on software development that are still working as separate entities rather than focusing on a common goal.",
            },
            {
              title: 'Deployment administration - ',
              text: "We're focusing on introducing the customer's infrastructure as a code responsible for time-saving and high-value rollback deliveries.",
            },
            {
              text: "In our daily operations, we employ DevOps as a service that's based mainly on AWS. It gives us 24/7 infrastructure availability and the flexibility necessary to improve Agile software implementation.",
            },
          ]}
        />
      </ImageDescription>
      <DevOpsCards />
      <ImageDescription
        bgImage={{ image: bgAboutUs }}
        image={{ image: solutionsImage, srcSet: `${solutionsImage} 1x, ${solutionsImage2x} 2x` }}
        upperTitle="Helping hand"
        title="Outsourced DevOps Solutions"
        text={
          "With the help of our outsourced DevOps coders, it will be more than achievable for you to stay above the competition that continues to implement changes and deploys IT products manually.\n\n The best DevOps practices speed up the development process as a whole, but it's even more important to understand that they grant the users unparalleled round-the-clock access to applications. That way, by automating your development process with the help of US Software Solutions' specialists, you will not only ease things up for yourself, but will win your customers' undivided allegiance."
        }
      />
      <ImageDescription
        bgImage={{ image: technologiesToolsBgSvg }}
        align="right"
        image={{ image: technologiesToolsImage }}
        upperTitle="Cutting-edge software"
        title="Technologies and Tools"
        text={
          'US Software Solutions DevOps experts employ a variety of innovative tools that ensure quality implementation, monitoring, delivery, logging, and infrastructure development:'
        }
      >
        <TechnologiesTools
          contents={[
            {
              title: 'Continuous integration and Delivery: ',
              text: 'Jenkins',
            },
            {
              title: 'Container: ',
              text: 'Docker',
            },
            {
              title: 'Automation: ',
              text: 'Ansible, Chef, Puppet',
            },
            {
              title: 'Clouds: ',
              text: 'AWS, Azure',
            },
            {
              title: 'Cluster managers: ',
              text: 'Kubernetes, Docker Swarm',
            },
            {
              title: 'Monitoring: ',
              text: 'New Relic, Application Insight, Keymetrics',
            },
          ]}
        />
      </ImageDescription>
      <RequestForm />
    </>
  );
};

export default ItInfrastructure;
